import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Truck, Plus, Search, MapPin, CheckCircle, AlertTriangle, 
  XCircle, ClipboardCheck, Calendar, Settings, BarChart3,
  ChevronRight, RefreshCw, Eye, Edit, Trash2, Power, PowerOff
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from '@/services/api';
import { toast } from 'sonner';
import { useApp } from '@/context/AppContext';

export default function Materiel() {
  const navigate = useNavigate();
  const { categoriesCACES } = useApp();
  const [materiel, setMateriel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [zoneFilter, setZoneFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMateriel, setSelectedMateriel] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    designation: '',
    type_materiel_id: '',
    categorie_caces_id: '',
    secteur_id: '',
    zone: '',
    marque: '',
    modele: '',
    numero_serie: '',
    date_mise_service: '',
    date_derniere_vgp: '',
    statut: 'actif'
  });
  
  // Dashboard data
  const [dashboardStats, setDashboardStats] = useState(null);
  const [vgpEcheances, setVgpEcheances] = useState([]);
  const [nonConformites, setNonConformites] = useState([]);
  const [typesMateriel, setTypesMateriel] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [materielData, statsData, echeancesData, nonConformitesData, typesData] = await Promise.all([
        api.getMateriel(),
        api.getMaterielDashboardStats().catch(() => null),
        api.getMaterielCTEcheances().catch(() => []),
        api.getMaterielNonConformites().catch(() => []),
        api.getTypesMateriel().catch(() => [])
      ]);
      setMateriel(materielData || []);
      setDashboardStats(statsData);
      setVgpEcheances(echeancesData || []);
      setNonConformites(nonConformitesData || []);
      setTypesMateriel(typesData || []);
    } catch (error) {
      toast.error('Erreur lors du chargement du matériel');
    } finally {
      setLoading(false);
    }
  };

  const zones = [...new Set(materiel.map(m => m.site || m.zone).filter(Boolean))];
  const types = [...new Set(materiel.map(m => m.type_materiel_libelle || m.type).filter(Boolean))];

  // Functions for CRUD operations
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        date_dernier_ct: formData.date_derniere_vgp,
        site: formData.zone || formData.site
      };
      
      if (selectedMateriel) {
        await api.updateMateriel(selectedMateriel.id, dataToSend);
        toast.success('Matériel mis à jour');
      } else {
        await api.createMateriel(dataToSend);
        toast.success('Matériel ajouté');
      }
      setDialogOpen(false);
      resetForm();
      await loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async () => {
    if (!selectedMateriel) return;
    try {
      await api.deleteMateriel(selectedMateriel.id);
      toast.success('Matériel supprimé');
      await loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedMateriel(null);
    }
  };

  const openEditDialog = (item) => {
    setSelectedMateriel(item);
    setFormData({
      code: item.code || '',
      designation: item.designation || '',
      type_materiel_id: item.type_materiel_id || '',
      categorie_caces_id: item.categorie_caces_id || '',
      secteur_id: item.secteur_id || '',
      zone: item.site || item.zone || '',
      marque: item.marque || '',
      modele: item.modele || '',
      numero_serie: item.numero_serie || '',
      date_mise_service: item.date_mise_service || '',
      date_derniere_vgp: item.date_dernier_ct || item.date_derniere_vgp || '',
      statut: item.statut || 'actif'
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      code: '',
      designation: '',
      type_materiel_id: '',
      categorie_caces_id: '',
      secteur_id: '',
      zone: '',
      marque: '',
      modele: '',
      numero_serie: '',
      date_mise_service: '',
      date_derniere_vgp: '',
      statut: 'actif'
    });
    setSelectedMateriel(null);
  };

  const toggleStatut = async (item) => {
    const newStatut = item.statut === 'actif' ? 'inactif' : 'actif';
    try {
      await api.updateMateriel(item.id, { statut: newStatut });
      toast.success(`Matériel ${newStatut === 'actif' ? 'activé' : 'désactivé'}`);
      await loadData();
    } catch (error) {
      toast.error('Erreur lors du changement de statut');
    }
  };

  const filteredMateriel = materiel.filter(mat => {
    const matchesSearch = !searchTerm ||
      mat.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mat.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mat.marque?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mat.designation?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesZone = zoneFilter === 'all' || (mat.site || mat.zone) === zoneFilter;
    const matchesType = typeFilter === 'all' || (mat.type_materiel_libelle || mat.type) === typeFilter;
    return matchesSearch && matchesZone && matchesType;
  });

  const getVGPStatus = (date) => {
    if (!date) return null;
    const prochaineVGP = new Date(date);
    const today = new Date();
    const diffTime = prochaineVGP - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return { className: 'bg-red-100 text-red-800 border-red-200', label: 'Échue', icon: XCircle, color: 'red' };
    if (diffDays <= 30) return { className: 'bg-orange-100 text-orange-800 border-orange-200', label: `J-${diffDays}`, icon: AlertTriangle, color: 'orange' };
    if (diffDays <= 60) return { className: 'bg-yellow-100 text-yellow-800 border-yellow-200', label: `J-${diffDays}`, icon: AlertTriangle, color: 'yellow' };
    return { className: 'bg-green-100 text-green-800 border-green-200', label: 'OK', icon: CheckCircle, color: 'green' };
  };

  const stats = dashboardStats || {
    total_materiel: materiel.length,
    actifs: materiel.filter(m => m.actif !== false).length,
    ct: {
      echu: materiel.filter(m => {
        if (!m.date_prochaine_ct && !m.date_fin_ct) return false;
        return new Date(m.date_prochaine_ct || m.date_fin_ct) < new Date();
      }).length,
      moins_30j: 0,
      moins_60j: 0,
      ok: 0
    },
    checklist: {
      materiel_verifies: 0,
      taux_conformite_moyen: 0,
      total_non_conformites: 0
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Truck className="w-7 h-7 text-blue-600" />
            Matériel Roulant
          </h1>
          <p className="text-gray-500 mt-1">
            {materiel.length} équipement{materiel.length > 1 ? 's' : ''} • {zones.length} zone{zones.length > 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={() => navigate('/types-materiel')}
          >
            <Settings className="w-4 h-4" />
            Types & Checklists
          </Button>
          <Link to="/gestion-materiel">
            <Button className="gap-2">
              <Plus className="w-4 h-4" />
              Gérer le matériel
            </Button>
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-flex">
          <TabsTrigger value="dashboard" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Tableau de bord
          </TabsTrigger>
          <TabsTrigger value="liste" className="gap-2">
            <Truck className="w-4 h-4" />
            Liste matériel
          </TabsTrigger>
          <TabsTrigger value="alertes" className="gap-2">
            <AlertTriangle className="w-4 h-4" />
            Alertes
            {(vgpEcheances.length > 0 || nonConformites.length > 0) && (
              <Badge variant="destructive" className="ml-1 h-5 px-1.5">
                {vgpEcheances.length + nonConformites.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Stats principales */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-full bg-blue-100">
                    <Truck className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total équipements</p>
                    <p className="text-3xl font-bold">{stats.total_materiel}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-full bg-green-100">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">VGP à jour</p>
                    <p className="text-3xl font-bold text-green-600">{stats.ct?.ok || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-full bg-orange-100">
                    <AlertTriangle className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">VGP à renouveler</p>
                    <p className="text-3xl font-bold text-orange-600">
                      {(stats.ct?.moins_30j || 0) + (stats.ct?.moins_60j || 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-full bg-red-100">
                    <XCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">VGP échues</p>
                    <p className="text-3xl font-bold text-red-600">{stats.ct?.echu || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Stats Checklist */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ClipboardCheck className="w-5 h-5 text-blue-600" />
                  Vérifications Sécurité
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Matériels vérifiés</span>
                  <span className="font-semibold">
                    {stats.checklist?.materiel_verifies || 0} / {stats.total_materiel}
                  </span>
                </div>
                <Progress 
                  value={stats.total_materiel > 0 ? ((stats.checklist?.materiel_verifies || 0) / stats.total_materiel * 100) : 0} 
                  className="h-2"
                />
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">
                      {stats.checklist?.taux_conformite_moyen || 0}%
                    </p>
                    <p className="text-xs text-gray-500">Taux conformité moyen</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">
                      {stats.checklist?.total_non_conformites || 0}
                    </p>
                    <p className="text-xs text-gray-500">Non-conformités</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Truck className="w-5 h-5 text-blue-600" />
                  Répartition par Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-48 overflow-y-auto">
                  {stats.types_materiel?.length > 0 ? (
                    stats.types_materiel.map((type, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-sm">{type.libelle}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full" 
                              style={{ width: `${(type.count / stats.total_materiel * 100)}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium w-8 text-right">{type.count}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">Aucune donnée</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* VGP à échéance proche */}
          {vgpEcheances.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-orange-600" />
                  VGP - Vérifications Périodiques à Échéance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Désignation</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Date VGP</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vgpEcheances.slice(0, 5).map((item) => (
                      <TableRow key={item.materiel_id}>
                        <TableCell className="font-medium">{item.code}</TableCell>
                        <TableCell>{item.designation || '-'}</TableCell>
                        <TableCell>{item.type_materiel}</TableCell>
                        <TableCell>{new Date(item.date_ct).toLocaleDateString('fr-FR')}</TableCell>
                        <TableCell>
                          <Badge className={
                            item.statut === 'echu' ? 'bg-red-100 text-red-800' :
                            item.statut === 'urgent' ? 'bg-orange-100 text-orange-800' :
                            'bg-yellow-100 text-yellow-800'
                          }>
                            {item.statut === 'echu' ? 'Échue' : `J-${item.jours_restants}`}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {vgpEcheances.length > 5 && (
                  <div className="text-center pt-2">
                    <Button variant="link" onClick={() => setActiveTab('alertes')}>
                      Voir tout ({vgpEcheances.length})
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Liste Tab */}
        <TabsContent value="liste" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Rechercher par code, type, marque..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-full md:w-48">
                    <SelectValue placeholder="Tous les types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les types</SelectItem>
                    {types.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={zoneFilter} onValueChange={setZoneFilter}>
                  <SelectTrigger className="w-full md:w-48">
                    <SelectValue placeholder="Toutes les zones" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les zones</SelectItem>
                    {zones.map((zone) => (
                      <SelectItem key={zone} value={zone}>{zone}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : filteredMateriel.length === 0 ? (
                <div className="text-center py-12">
                  <Truck className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">Aucun matériel trouvé</p>
                  <Link to="/gestion-materiel">
                    <Button variant="link">Ajouter un équipement</Button>
                  </Link>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Désignation</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Marque / Modèle</TableHead>
                      <TableHead>Zone</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>VGP</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMateriel.map((mat) => {
                      const vgpStatus = getVGPStatus(mat.date_prochaine_ct || mat.date_fin_ct);
                      return (
                        <TableRow key={mat.id} className={mat.statut === 'inactif' ? 'opacity-60 bg-gray-50' : ''}>
                          <TableCell className="font-medium">{mat.code}</TableCell>
                          <TableCell>{mat.designation || '-'}</TableCell>
                          <TableCell>{mat.type_materiel_libelle || mat.type || '-'}</TableCell>
                          <TableCell>
                            {mat.marque && mat.modele ? `${mat.marque} ${mat.modele}` : mat.marque || mat.modele || '-'}
                          </TableCell>
                          <TableCell>
                            {(mat.site || mat.zone) ? (
                              <Badge variant="outline">
                                <MapPin className="h-3 w-3 mr-1" />
                                {mat.site || mat.zone}
                              </Badge>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              className={mat.statut === 'inactif' 
                                ? 'bg-gray-100 text-gray-700 cursor-pointer hover:bg-gray-200' 
                                : 'bg-green-100 text-green-700 cursor-pointer hover:bg-green-200'
                              }
                              onClick={() => toggleStatut(mat)}
                            >
                              {mat.statut === 'inactif' ? (
                                <><PowerOff className="h-3 w-3 mr-1" />Inactif</>
                              ) : (
                                <><Power className="h-3 w-3 mr-1" />Actif</>
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {(mat.date_prochaine_ct || mat.date_fin_ct) ? (
                              <div className="flex items-center gap-2">
                                <span className="text-sm">
                                  {new Date(mat.date_prochaine_ct || mat.date_fin_ct).toLocaleDateString('fr-FR')}
                                </span>
                                {vgpStatus && (
                                  <Badge className={vgpStatus.className}>
                                    {vgpStatus.label}
                                  </Badge>
                                )}
                              </div>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEditDialog(mat)} title="Modifier">
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Link to={`/materiel/${mat.id}/checklist`}>
                                <Button variant="ghost" size="icon" title="Checklist">
                                  <ClipboardCheck className="w-4 h-4" />
                                </Button>
                              </Link>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => {
                                  setSelectedMateriel(mat);
                                  setDeleteDialogOpen(true);
                                }}
                                title="Supprimer"
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alertes Tab */}
        <TabsContent value="alertes" className="space-y-6">
          {/* VGP à échéance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-orange-600" />
                VGP - Vérifications Périodiques à Échéance ({vgpEcheances.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {vgpEcheances.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
                  <p>Aucune vérification périodique à échéance dans les 60 prochains jours</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Désignation</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Date VGP</TableHead>
                      <TableHead>Jours restants</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vgpEcheances.map((item) => (
                      <TableRow key={item.materiel_id} className={item.statut === 'echu' ? 'bg-red-50' : ''}>
                        <TableCell className="font-medium">{item.code}</TableCell>
                        <TableCell>{item.designation || '-'}</TableCell>
                        <TableCell>{item.type_materiel}</TableCell>
                        <TableCell>{new Date(item.date_ct).toLocaleDateString('fr-FR')}</TableCell>
                        <TableCell>
                          <span className={
                            item.jours_restants < 0 ? 'text-red-600 font-bold' :
                            item.jours_restants <= 30 ? 'text-orange-600 font-bold' :
                            'text-yellow-600'
                          }>
                            {item.jours_restants < 0 ? `${Math.abs(item.jours_restants)} jours de retard` : `${item.jours_restants} jours`}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            item.statut === 'echu' ? 'bg-red-100 text-red-800' :
                            item.statut === 'urgent' ? 'bg-orange-100 text-orange-800' :
                            'bg-yellow-100 text-yellow-800'
                          }>
                            {item.statut === 'echu' ? 'ÉCHUE' : item.statut === 'urgent' ? 'URGENT' : 'ATTENTION'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Non-conformités */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-600" />
                Non-Conformités Checklist ({nonConformites.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {nonConformites.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
                  <p>Aucune non-conformité détectée</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Désignation</TableHead>
                      <TableHead>Non-conformités</TableHead>
                      <TableHead>Taux conformité</TableHead>
                      <TableHead>Vérifié par</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {nonConformites.map((item) => (
                      <TableRow key={item.materiel_id} className="bg-red-50">
                        <TableCell className="font-medium">{item.code}</TableCell>
                        <TableCell>{item.designation || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="destructive">{item.non_conformes} élément(s)</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={item.taux_conformite} className="w-16 h-2" />
                            <span className="text-sm">{item.taux_conformite}%</span>
                          </div>
                        </TableCell>
                        <TableCell>{item.verifie_par}</TableCell>
                        <TableCell>
                          {item.date_verification ? new Date(item.date_verification).toLocaleDateString('fr-FR') : '-'}
                        </TableCell>
                        <TableCell>
                          <Link to={`/materiel/${item.materiel_id}/checklist`}>
                            <Button variant="outline" size="sm">
                              <Eye className="w-4 h-4 mr-1" />
                              Voir
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedMateriel ? 'Modifier le matériel' : 'Nouveau matériel'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="CH-CE-001"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Désignation *</Label>
                <Input
                  value={formData.designation}
                  onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                  placeholder="Chariot élévateur frontal"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Type de matériel *</Label>
                <Select 
                  value={formData.type_materiel_id} 
                  onValueChange={(value) => setFormData({ ...formData, type_materiel_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un type" />
                  </SelectTrigger>
                  <SelectContent>
                    {typesMateriel.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.libelle}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Zone</Label>
                <Input
                  value={formData.zone}
                  onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                  placeholder="Zone A, Entrepôt 1, etc."
                />
              </div>
              <div className="space-y-2">
                <Label>Catégorie CACES requise</Label>
                <Select 
                  value={formData.categorie_caces_id} 
                  onValueChange={(value) => setFormData({ ...formData, categorie_caces_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoriesCACES?.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.code} - {cat.libelle}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Numéro de série</Label>
                <Input
                  value={formData.numero_serie}
                  onChange={(e) => setFormData({ ...formData, numero_serie: e.target.value })}
                  placeholder="SN123456789"
                />
              </div>
              <div className="space-y-2">
                <Label>Marque</Label>
                <Input
                  value={formData.marque}
                  onChange={(e) => setFormData({ ...formData, marque: e.target.value })}
                  placeholder="Fenwick"
                />
              </div>
              <div className="space-y-2">
                <Label>Modèle</Label>
                <Input
                  value={formData.modele}
                  onChange={(e) => setFormData({ ...formData, modele: e.target.value })}
                  placeholder="H25T"
                />
              </div>
              <div className="space-y-2">
                <Label>Date de mise en service</Label>
                <Input
                  type="date"
                  value={formData.date_mise_service}
                  onChange={(e) => setFormData({ ...formData, date_mise_service: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Date de la dernière VGP</Label>
                <Input
                  type="date"
                  value={formData.date_derniere_vgp}
                  onChange={(e) => setFormData({ ...formData, date_derniere_vgp: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select 
                  value={formData.statut || 'actif'} 
                  onValueChange={(value) => setFormData({ ...formData, statut: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner le statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="actif">
                      <span className="flex items-center gap-2">
                        <Power className="h-4 w-4 text-green-600" />
                        Actif
                      </span>
                    </SelectItem>
                    <SelectItem value="inactif">
                      <span className="flex items-center gap-2">
                        <PowerOff className="h-4 w-4 text-gray-500" />
                        Inactif
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit">
                {selectedMateriel ? 'Mettre à jour' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer le matériel <strong>{selectedMateriel?.code}</strong> ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
